import React, { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '', // Honeypot field - must be empty
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: '',
        });
      } else {
        const data = await response.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(data.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setStatus('error');
      setErrorMessage('Der kunne ikke oprettes forbindelse til serveren. Tjek din internetforbindelse eller ring direkte til os.');
    }
  };

  return (
    <div className="bg-white border border-taupe-border/80 p-6 sm:p-8 lg:p-10 shadow-xs">
      <div className="mb-6">
        <h3 className="font-sans text-xl sm:text-2xl font-semibold text-espresso uppercase tracking-wider">
          Send en besked til klinikken
        </h3>
        <p className="text-sm text-taupe mt-1.5 leading-relaxed">
          Har du spørgsmål til en behandling eller et individuelt forløb? Udfyld formularen, så vender vi hurtigt tilbage.
        </p>
      </div>

      {status === 'success' ? (
        <div className="p-6 bg-sand/30 border border-taupe-border text-center space-y-3 animate-in fade-in duration-300">
          <div className="text-espresso text-3xl mx-auto mb-2">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h4 className="font-sans text-lg font-semibold text-espresso uppercase tracking-wider">
            Tak for din henvendelse
          </h4>
          <p className="text-sm text-taupe max-w-md mx-auto">
            Vi har modtaget din besked og kontakter dig snarest muligt.
          </p>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="mt-4 px-6 py-2.5 bg-accent-brown text-white text-xs uppercase tracking-wider font-semibold hover:bg-accent-brown-hover transition-colors cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Antispam honeypot field - strictly hidden */}
          <div className="sr-only absolute -left-[9999px] -top-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website URL (leave blank)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-espresso mb-1">
                Navn <span className="text-accent-brown">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className="w-full px-0 py-2.5 bg-transparent border-0 border-b border-espresso/35 text-espresso placeholder:text-taupe-light/70 focus:outline-hidden focus:border-b-espresso focus:ring-0 transition-colors text-sm font-normal"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-espresso mb-1">
                Telefonnummer <span className="text-accent-brown">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 26 43 44 02"
                className="w-full px-0 py-2.5 bg-transparent border-0 border-b border-espresso/35 text-espresso placeholder:text-taupe-light/70 focus:outline-hidden focus:border-b-espresso focus:ring-0 transition-colors text-sm font-normal"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-espresso mb-1">
              Email <span className="text-accent-brown">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="din@email.dk"
              className="w-full px-0 py-2.5 bg-transparent border-0 border-b border-espresso/35 text-espresso placeholder:text-taupe-light/70 focus:outline-hidden focus:border-b-espresso focus:ring-0 transition-colors text-sm font-normal"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-espresso mb-1">
              Beskrivelse / Besked <span className="text-accent-brown">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={4}
              value={formData.message}
              onChange={handleChange}
              placeholder="Skriv din besked, spørgsmål eller ønsker til behandling her..."
              className="w-full px-0 py-2.5 bg-transparent border-0 border-b border-espresso/35 text-espresso placeholder:text-taupe-light/70 focus:outline-hidden focus:border-b-espresso focus:ring-0 transition-colors text-sm font-normal resize-y"
            ></textarea>
          </div>

          {status === 'error' && (
            <div className="p-3.5 bg-red-50 text-red-800 text-xs flex items-center gap-2 border border-red-200">
              <i className="fa-solid fa-circle-exclamation text-sm shrink-0" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-accent-brown text-white text-xs font-semibold uppercase tracking-widest hover:bg-accent-brown-hover transition-colors shadow-xs disabled:opacity-50 cursor-pointer focus:outline-hidden"
            >
              {status === 'submitting' ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin text-xs" aria-hidden="true" />
                  <span>Sender besked...</span>
                </>
              ) : (
                <>
                  <span>Send besked</span>
                  <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
